import React, { CSSProperties, useCallback, useState } from 'react'

import classNames from 'classnames'
import styled from 'styled-components'

const IconButtonBase = styled.div`
  position: absolute;

  background-color: white;
  transition: background-color 0.3s;
  padding: 8px;
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.1);
  border-radius: 2px;

  display: flex;
  justify-content: center;
  align-items: center;

  cursor: pointer;
  user-select: none;

  &.primary {
    background-color: #073c7a;
  }

  &.disabled {
    color: #92989f;
  }
`

interface IconButtonProps {
  icon?: string
  /** Inline SVG or image; takes precedence over `icon` when set. */
  iconElement?: React.ReactNode
  hoverIcon?: string
  size: number
  tooltip?: string
  primary?: boolean
  disabled?: boolean
  style?: CSSProperties
  hoverStyle?: CSSProperties
  classNames?: string
  onClick?: () => void
}

export function IconButton({
  icon,
  iconElement,
  hoverIcon,
  size,
  tooltip,
  primary,
  disabled,
  style,
  hoverStyle,
  classNames: classNamesProps,
  onClick,
}: IconButtonProps) {
  const [isHovering, setIsHovering] = useState<boolean>(false)

  const baseStyles: CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    position: 'relative',
  }

  const currentIcon: string | undefined =
    icon && hoverIcon && isHovering ? hoverIcon : icon

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (disabled) {
        return
      }
      if (onClick) onClick()
    },
    [disabled, onClick],
  )

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
    },
    [],
  )

  return (
    <IconButtonBase
      style={{ ...baseStyles, ...style, ...(isHovering ? hoverStyle : {}) }}
      className={classNames({ primary, disabled }) + ' ' + classNamesProps}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onDoubleClickCapture={handleDoubleClick} // Prevent double click to be captured by Pannellum
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      data-tip={tooltip}
    >
      {iconElement ?? (currentIcon ? <img src={currentIcon} alt="" /> : null)}
    </IconButtonBase>
  )
}
